import { Button } from "@babackapp/ui/components/button";
import { Input, InputProps } from "@babackapp/ui/components/input";
import { Cross1Icon, FileIcon } from "@radix-ui/react-icons";
import { forwardRef, useCallback, useRef } from "react";
import FormInputContainer from "./form-input-container";

type FormFileInputProps = Omit<InputProps, "disabled" | "type"> & {
  label: string;
  description?: string;
  disabled?: boolean | string;
  accept?: string;
  multiple?: boolean;
  fileName?: string | null;
  onChange?: (files: FileList | null) => void;
};

const FormFileInput = forwardRef(function FormFileInput(
  {
    label,
    description,
    disabled,
    accept,
    multiple,
    fileName,
    onChange,
    ...props
  }: FormFileInputProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.files);
    },
    [onChange],
  );

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <FormInputContainer
      disabled={disabled}
      label={label}
      description={description}
    >
      <div className="relative">
        <Input
          ref={ref}
          {...props}
          type="text"
          readOnly
          onClick={handleClick}
          value={fileName ?? (fileInputRef.current?.value || "")}
          disabled={!!disabled}
          className="relative pl-8"
        />
        <FileIcon className="absolute top-1/2 left-2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          hidden
          onChange={handleChange}
          accept={accept}
          multiple={multiple}
          disabled={!!disabled}
        />
        {fileInputRef.current?.value && (
          <Button
            size="sm"
            variant="ghost"
            className="absolute top-1/2 right-1 py-2 h-auto transform -translate-y-1/2"
            type="button"
            onClick={() => {
              fileInputRef.current!.value = "";
              onChange?.(null);
            }}
          >
            <Cross1Icon className="size-4 text-gray-400" />
          </Button>
        )}
      </div>
    </FormInputContainer>
  );
});

FormFileInput.displayName = "FormFileInput";

export default FormFileInput;
